import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { MapPin, Filter, X, Loader2, AlertTriangle, Pencil, Sparkles } from "lucide-react";
import "maplibre-gl/dist/maplibre-gl.css";
import { crmListCities } from "@/lib/crm.functions";
import { crmListAllLocations, crmGeocodeUnmapped } from "@/lib/crm-locations.functions";
import { MAPLIBRE_OSM_STYLE } from "@/lib/map-style";
import { useAuth } from "@/hooks/use-auth";

type MapSearch = { city: string; seg: string; sub: string; q: string };

export const Route = createFileRoute("/crm/map")({
  validateSearch: (raw: Record<string, unknown> & SearchSchemaInput): MapSearch => ({
    city: typeof raw.city === "string" ? raw.city : "",
    seg: typeof raw.seg === "string" ? raw.seg : "",
    sub: typeof raw.sub === "string" ? raw.sub : "",
    q: typeof raw.q === "string" ? raw.q : "",
  }),
  component: CrmMapPage,
});

const SEGMENTS = [
  { value: "eat_drink", label: "Eat & Drink" },
  { value: "do", label: "Do" },
  { value: "happenings", label: "Happenings" },
] as const;

type Seg = (typeof SEGMENTS)[number]["value"];

type MLMap = import("maplibre-gl").Map;

type Loc = {
  id: string;
  client_id: string;
  label: string;
  address: string | null;
  city: string | null;
  lat: number | null;
  lng: number | null;
  is_primary: boolean;
};

type Client = {
  id: string;
  name: string;
  company_name: string | null;
  city_id: string | null;
  segment: Seg | null;
  subcategory: string | null;
};

function CrmMapPage() {
  const { user, loading } = useAuth();
  const listCities = useServerFn(crmListCities);
  const listAll = useServerFn(crmListAllLocations);

  const sp = Route.useSearch();
  const navigate = useNavigate({ from: "/crm/map" });
  const cityId = sp.city || null;
  const segment = (SEGMENTS.find((s) => s.value === sp.seg)?.value ?? null) as Seg | null;
  const subcategory = sp.sub;
  const q = sp.q;

  const patchSearch = useCallback(
    (patch: Partial<MapSearch>) => {
      void navigate({
        replace: true,
        search: (prev: MapSearch) => ({ ...prev, ...patch }),
      });
    },
    [navigate],
  );

  const setCityId = (v: string | null) => patchSearch({ city: v ?? "" });
  const setSegment = (v: Seg | null) => patchSearch({ seg: v ?? "" });
  const setSubcategory = (v: string) => patchSearch({ sub: v });

  // Local input state for the search box (debounced into URL).
  const [search, setSearch] = useState(q);
  useEffect(() => setSearch(q), [q]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== q) patchSearch({ q: search.trim() });
    }, 300);
    return () => clearTimeout(t);
  }, [search, q, patchSearch]);
  const debounced = q;

  const citiesQ = useQuery({
    queryKey: ["crm", "cities"],
    queryFn: () => listCities(),
    enabled: !!user,
    staleTime: 15 * 60_000,
  });
  const cities = citiesQ.data?.cities ?? [];

  const dataQ = useQuery({
    queryKey: ["crm", "map", { cityId, segment, subcategory, search: debounced }],
    queryFn: () =>
      listAll({
        data: {
          city_id: cityId,
          segment: segment ?? undefined,
          subcategory: subcategory.trim() || undefined,
          search: debounced || undefined,
        },
      }),
    enabled: !!user,
    staleTime: 60_000,
  });

  const clients: Client[] = (dataQ.data?.clients as Client[]) ?? [];
  const allLocations: Loc[] = (dataQ.data?.locations as Loc[]) ?? [];
  const locations = useMemo(
    () => allLocations.filter((l): l is Loc & { lat: number; lng: number } => l.lat != null && l.lng != null),
    [allLocations],
  );
  const unmapped = useMemo(
    () => allLocations.filter((l) => l.lat == null || l.lng == null),
    [allLocations],
  );

  const clientById = useMemo(() => {
    const m = new Map<string, Client>();
    clients.forEach((c) => m.set(c.id, c));
    return m;
  }, [clients]);

  // Suggest subcategories from the currently loaded clients.
  const subcatOptions = useMemo(() => {
    const s = new Set<string>();
    clients.forEach((c) => c.subcategory && s.add(c.subcategory));
    return Array.from(s).sort();
  }, [clients]);

  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  // Counts + sorted client list for legend.
  const clientStats = useMemo(() => {
    const counts = new Map<string, number>();
    locations.forEach((l) => counts.set(l.client_id, (counts.get(l.client_id) ?? 0) + 1));
    return Array.from(counts.entries())
      .map(([id, count]) => ({ client: clientById.get(id), id, count }))
      .filter((r) => r.client)
      .sort((a, b) => (a.client!.name ?? "").localeCompare(b.client!.name ?? ""));
  }, [locations, clientById]);
  const clientCount = clientStats.length;

  // Clear selection when it disappears from the filtered result.
  useEffect(() => {
    if (selectedClientId && !clientById.has(selectedClientId)) setSelectedClientId(null);
  }, [selectedClientId, clientById]);

  const activeFilters = [
    cityId && { key: "city", label: cities.find((c: any) => c.id === cityId)?.name ?? "City", clear: () => setCityId(null) },
    segment && { key: "seg", label: SEGMENTS.find((s) => s.value === segment)?.label ?? segment, clear: () => setSegment(null) },
    subcategory && { key: "sub", label: subcategory, clear: () => setSubcategory("") },
    search && { key: "q", label: `"${search}"`, clear: () => setSearch("") },
  ].filter(Boolean) as { key: string; label: string; clear: () => void }[];

  if (loading) {
    return (
      <div className="grid place-items-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">

        <section className="rounded-xl border border-[#e7eaf0] bg-white shadow-sm p-3 sm:p-4 space-y-3">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#0d6efd]">
            <Filter className="h-4 w-4" /> Filters
            {activeFilters.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setCityId(null);
                  setSegment(null);
                  setSubcategory("");
                  setSearch("");
                }}
                className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-[#0d6efd] hover:bg-[#e7f1ff]"
              >
                <X className="h-3 w-3" /> Clear
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <select
              value={cityId ?? ""}
              onChange={(e) => setCityId(e.target.value || null)}
              className="h-9 rounded-lg border border-[#e7eaf0] bg-white px-2 text-sm"
            >
              <option value="">All cities</option>
              {cities.map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            <select
              value={segment ?? ""}
              onChange={(e) => setSegment((e.target.value as Seg) || null)}
              className="h-9 rounded-lg border border-[#e7eaf0] bg-white px-2 text-sm"
            >
              <option value="">All segments</option>
              {SEGMENTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>

            <input
              list="crm-map-subcats"
              value={subcategory}
              onChange={(e) => setSubcategory(e.target.value)}
              placeholder="Category (e.g. Burger)"
              className="h-9 rounded-lg border border-[#e7eaf0] bg-white px-3 text-sm"
            />
            <datalist id="crm-map-subcats">
              {subcatOptions.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or company…"
              className="h-9 rounded-lg border border-[#e7eaf0] bg-white px-3 text-sm"
            />
          </div>

          <div className="flex items-center gap-3 text-xs text-[#6c757d]">
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {locations.length} location{locations.length === 1 ? "" : "s"}
            </span>
            <span>·</span>
            <span>{clientCount} client{clientCount === 1 ? "" : "s"}</span>
            {dataQ.isFetching && (
              <>
                <span>·</span>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              </>
            )}
          </div>
        </section>

        <MappedSummary
          mapped={locations.length}
          unmapped={unmapped.length}
          clientCount={clientCount}
          activeFilterCount={activeFilters.length}
        />

        {unmapped.length > 0 && (
          <UnmappedWarning
            unmapped={unmapped}
            clientById={clientById}
            onDone={() => dataQ.refetch()}
          />
        )}

        <section className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">
          <div className="rounded-xl border border-[#e7eaf0] bg-white shadow-sm overflow-hidden">
            <MultiClientMap
              locations={locations}
              clientById={clientById}
              selectedClientId={selectedClientId}
              onSelect={setSelectedClientId}
            />
          </div>
          <aside className="rounded-xl border border-[#e7eaf0] bg-white shadow-sm p-3 flex flex-col max-h-[70vh]">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#495057]">
                Legend · Clients
              </div>
              {selectedClientId && (
                <button
                  type="button"
                  onClick={() => setSelectedClientId(null)}
                  className="text-[11px] font-semibold text-[#0d6efd] hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="text-[11px] text-[#6c757d] mb-2">
              Each client has one color. Click to highlight all of their pins.
              Larger pins are the primary location.
            </p>
            {clientStats.length === 0 ? (
              <div className="text-xs text-[#6c757d] py-4 text-center">
                No clients match the current filters.
              </div>
            ) : (
              <ul className="space-y-1 overflow-y-auto pr-1 -mr-1">
                {clientStats.map(({ client, id, count }) => {
                  const isSel = selectedClientId === id;
                  const dim = selectedClientId && !isSel;
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        onClick={() => setSelectedClientId(isSel ? null : id)}
                        className={`w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors ${
                          isSel
                            ? "bg-[#e7f1ff] ring-1 ring-[#0d6efd]/30"
                            : "hover:bg-[#f4f6fb]"
                        } ${dim ? "opacity-60" : ""}`}
                      >
                        <span
                          className="h-3 w-3 rounded-full shrink-0 border-2 border-white shadow"
                          style={{ background: colorFor(id) }}
                        />
                        <span className="flex-1 min-w-0 truncate font-medium text-[#1a2540]">
                          {client!.name}
                        </span>
                        <span className="text-[10px] font-semibold text-[#6c757d]">
                          {count}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>
        </section>
    </div>
  );
}

// Deterministic per-client color from a small palette.
const PALETTE = [
  "#2563eb", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6",
  "#ec4899", "#14b8a6", "#f97316", "#0ea5e9", "#84cc16",
];
function colorFor(clientId: string) {
  let h = 0;
  for (let i = 0; i < clientId.length; i++) h = (h * 31 + clientId.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

const SOURCE_ID = "crm-locations";
const CLUSTER_MAX_ZOOM = 14;

function MultiClientMap({
  locations,
  clientById,
  selectedClientId,
  onSelect,
}: {
  locations: (Loc & { lat: number; lng: number })[];
  clientById: Map<string, Client>;
  selectedClientId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const modRef = useRef<any>(null);
  const styleReadyRef = useRef(false);
  const spiderMarkersRef = useRef<any[]>([]);
  const popupRef = useRef<any>(null);
  const selectedRef = useRef<string | null>(selectedClientId);
  selectedRef.current = selectedClientId;
  const clientByIdRef = useRef(clientById);
  clientByIdRef.current = clientById;

  const clearSpider = useCallback(() => {
    spiderMarkersRef.current.forEach((m) => m.remove());
    spiderMarkersRef.current = [];
  }, []);

  const featureCollection = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: locations.map((l) => ({
        type: "Feature" as const,
        properties: {
          id: l.id,
          client_id: l.client_id,
          label: l.label,
          address: l.address ?? "",
          is_primary: !!l.is_primary,
          color: colorFor(l.client_id),
        },
        geometry: { type: "Point" as const, coordinates: [l.lng, l.lat] },
      })),
    }),
    [locations],
  );

  const showPopup = useCallback(
    (lngLat: [number, number], props: any) => {
      const mod = modRef.current;
      const map = mapRef.current;
      if (!mod || !map) return;
      popupRef.current?.remove();
      const client = clientByIdRef.current.get(props.client_id);
      const html = `<div style="font-family:inherit;min-width:160px">
        <div style="font-weight:600;font-size:13px">${escapeHtml(client?.name ?? "Client")}</div>
        <div style="font-size:11px;color:#666;margin-top:2px">${escapeHtml(props.label ?? "")}</div>
        <div style="font-size:11px;color:#666">${escapeHtml(props.address ?? "")}</div>
        ${client?.subcategory ? `<div style="font-size:10px;color:#888;margin-top:4px">${escapeHtml(client.subcategory)}</div>` : ""}
      </div>`;
      popupRef.current = new mod.Popup({ offset: 14, closeButton: false })
        .setLngLat(lngLat)
        .setHTML(html)
        .addTo(map);
    },
    [],
  );

  // Init map once.
  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    let cancelled = false;
    void import("maplibre-gl").then((mod) => {
      if (cancelled || !ref.current) return;
      modRef.current = mod;
      const map = new mod.Map({
        container: ref.current,
        style: MAPLIBRE_OSM_STYLE,
        center: [24.9384, 60.1699],
        zoom: 5,
        attributionControl: false,
      });
      mapRef.current = map;

      map.on("load", () => {
        map.addSource(SOURCE_ID, {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
          cluster: true,
          clusterRadius: 50,
          clusterMaxZoom: CLUSTER_MAX_ZOOM,
        });

        map.addLayer({
          id: "clusters",
          type: "circle",
          source: SOURCE_ID,
          filter: ["has", "point_count"],
          paint: {
            "circle-color": [
              "step", ["get", "point_count"],
              "#60a5fa", 10, "#3b82f6", 50, "#1d4ed8",
            ],
            "circle-radius": [
              "step", ["get", "point_count"],
              16, 10, 20, 50, 26,
            ],
            "circle-stroke-width": 3,
            "circle-stroke-color": "#ffffff",
            "circle-opacity": 0.9,
          },
        });
        map.addLayer({
          id: "cluster-count",
          type: "symbol",
          source: SOURCE_ID,
          filter: ["has", "point_count"],
          layout: {
            "text-field": ["get", "point_count_abbreviated"],
            "text-size": 12,
            "text-font": ["Noto Sans Regular"],
          },
          paint: { "text-color": "#ffffff" },
        });
        map.addLayer({
          id: "points",
          type: "circle",
          source: SOURCE_ID,
          filter: ["!", ["has", "point_count"]],
          paint: {
            "circle-color": ["get", "color"],
            "circle-radius": [
              "case",
              ["==", ["get", "client_id"], ["literal", selectedRef.current ?? ""]],
              10,
              ["get", "is_primary"], 8,
              6,
            ],
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
            "circle-opacity": 1,
          },
        });

        // Cluster click → zoom in, or spiderfy if at max cluster zoom.
        map.on("click", "clusters", (e: any) => {
          const feat = e.features?.[0];
          if (!feat) return;
          const src: any = map.getSource(SOURCE_ID);
          const clusterId = feat.properties.cluster_id;
          src.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
            if (err) return;
            const at = map.getZoom();
            if (zoom > CLUSTER_MAX_ZOOM && at >= CLUSTER_MAX_ZOOM) {
              // Spiderfy.
              src.getClusterLeaves(
                clusterId, feat.properties.point_count, 0,
                (lErr: any, leaves: any[]) => {
                  if (lErr) return;
                  spiderfy(map, mod, feat.geometry.coordinates, leaves, {
                    onSelect,
                    selectedRef,
                    showPopup,
                    markersRef: spiderMarkersRef,
                  });
                },
              );
            } else {
              clearSpider();
              map.easeTo({
                center: feat.geometry.coordinates,
                zoom: Math.min(zoom, CLUSTER_MAX_ZOOM + 1),
                duration: 400,
              });
            }
          });
        });

        map.on("click", "points", (e: any) => {
          const feat = e.features?.[0];
          if (!feat) return;
          const props = feat.properties;
          const coords = feat.geometry.coordinates.slice() as [number, number];
          showPopup(coords, props);
          onSelect(selectedRef.current === props.client_id ? null : props.client_id);
        });

        map.on("click", (e: any) => {
          // Empty-area click clears spider.
          const hits = map.queryRenderedFeatures(e.point, {
            layers: ["clusters", "points"],
          });
          if (!hits.length) clearSpider();
        });

        for (const layer of ["clusters", "points"]) {
          map.on("mouseenter", layer, () => (map.getCanvas().style.cursor = "pointer"));
          map.on("mouseleave", layer, () => (map.getCanvas().style.cursor = ""));
        }

        map.on("zoomstart", clearSpider);
        map.on("movestart", clearSpider);

        styleReadyRef.current = true;
        // Push initial data.
        (map.getSource(SOURCE_ID) as any).setData(featureCollection);
      });
    });
    return () => {
      cancelled = true;
      clearSpider();
      popupRef.current?.remove();
      mapRef.current?.remove();
      mapRef.current = null;
      styleReadyRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push data changes + fit.
  useEffect(() => {
    const map = mapRef.current;
    const mod = modRef.current;
    if (!map || !mod) return;
    const apply = () => {
      const src = map.getSource(SOURCE_ID) as any;
      if (!src) return;
      src.setData(featureCollection);
      clearSpider();
      if (!locations.length) return;
      if (locations.length === 1) {
        map.easeTo({ center: [locations[0].lng, locations[0].lat], zoom: 13, duration: 400 });
      } else {
        const b = new mod.LngLatBounds();
        locations.forEach((l) => b.extend([l.lng, l.lat]));
        map.fitBounds(b, { padding: 50, maxZoom: 14, duration: 400 });
      }
    };
    if (styleReadyRef.current) apply();
    else map.once("load", apply);
  }, [featureCollection, locations, clearSpider]);

  // Selection styling (paint updates) + fit to selected.
  useEffect(() => {
    const map = mapRef.current;
    const mod = modRef.current;
    if (!map || !mod || !styleReadyRef.current) return;
    const sel = selectedClientId ?? "";
    map.setPaintProperty("points", "circle-radius", [
      "case",
      ["==", ["get", "client_id"], ["literal", sel]], 11,
      ["get", "is_primary"], 8,
      6,
    ]);
    map.setPaintProperty("points", "circle-stroke-color", [
      "case",
      ["==", ["get", "client_id"], ["literal", sel]], "#0d6efd",
      "#ffffff",
    ]);
    map.setPaintProperty("points", "circle-stroke-width", [
      "case",
      ["==", ["get", "client_id"], ["literal", sel]], 3,
      2,
    ]);
    map.setPaintProperty("points", "circle-opacity", sel
      ? ["case", ["==", ["get", "client_id"], ["literal", sel]], 1, 0.25]
      : 1);
    map.setPaintProperty("clusters", "circle-opacity", sel ? 0.4 : 0.9);

    if (selectedClientId) {
      const pts = locations.filter((l) => l.client_id === selectedClientId);
      if (pts.length === 1) {
        map.easeTo({ center: [pts[0].lng, pts[0].lat], zoom: Math.max(map.getZoom(), 13), duration: 500 });
      } else if (pts.length > 1) {
        const b = new mod.LngLatBounds();
        pts.forEach((p) => b.extend([p.lng, p.lat]));
        map.fitBounds(b, { padding: 80, maxZoom: 14, duration: 500 });
      }
    }
  }, [selectedClientId, locations]);

  return <div ref={ref} className="h-[70vh] w-full" />;
}

// Spiderfy: fan cluster children out on a circle around the cluster centroid.
function spiderfy(
  map: any,
  mod: any,
  center: [number, number],
  leaves: any[],
  ctx: {
    onSelect: (id: string | null) => void;
    selectedRef: React.MutableRefObject<string | null>;
    showPopup: (lngLat: [number, number], props: any) => void;
    markersRef: React.MutableRefObject<any[]>;
  },
) {
  // Clear previous spider markers.
  ctx.markersRef.current.forEach((m) => m.remove());
  ctx.markersRef.current = [];

  const n = leaves.length;
  if (!n) return;
  const radiusPx = Math.max(40, Math.min(80, 20 + n * 4));
  const centerPx = map.project(center);
  leaves.forEach((leaf: any, i: number) => {
    const angle = (2 * Math.PI * i) / n - Math.PI / 2;
    const x = centerPx.x + radiusPx * Math.cos(angle);
    const y = centerPx.y + radiusPx * Math.sin(angle);
    const lngLat = map.unproject([x, y]);
    const p = leaf.properties;
    const el = document.createElement("div");
    el.style.cssText = `width:14px;height:14px;border-radius:9999px;background:${p.color};border:2px solid #fff;box-shadow:0 3px 8px rgba(0,0,0,.35);cursor:pointer;`;
    el.addEventListener("click", (e) => {
      e.stopPropagation();
      ctx.showPopup([lngLat.lng, lngLat.lat], p);
      ctx.onSelect(ctx.selectedRef.current === p.client_id ? null : p.client_id);
    });
    const marker = new mod.Marker({ element: el, anchor: "center" })
      .setLngLat(lngLat)
      .addTo(map);
    ctx.markersRef.current.push(marker);
  });
}


function escapeHtml(s: string) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function MappedSummary({
  mapped,
  unmapped,
  clientCount,
  activeFilterCount,
}: {
  mapped: number;
  unmapped: number;
  clientCount: number;
  activeFilterCount: number;
}) {
  const total = mapped + unmapped;
  const pct = total === 0 ? 0 : Math.round((mapped / total) * 100);
  return (
    <section className="rounded-xl border border-[#e7eaf0] bg-white shadow-sm px-3 sm:px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
      <div className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full bg-emerald-500" />
        <span className="font-semibold text-[#1a2540]">{mapped}</span>
        <span className="text-[#6c757d]">mapped</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full bg-amber-500" />
        <span className="font-semibold text-[#1a2540]">{unmapped}</span>
        <span className="text-[#6c757d]">unmapped</span>
      </div>
      <div className="text-[#6c757d]">
        of <span className="font-semibold text-[#1a2540]">{total}</span> total ·{" "}
        {clientCount} client{clientCount === 1 ? "" : "s"}
      </div>
      <div className="ml-auto flex items-center gap-2">
        <span className="text-[10px] font-semibold text-[#6c757d]">{pct}% mapped</span>
        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-[#e7eaf0]">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        {activeFilterCount > 0 && (
          <span className="rounded-md bg-[#e7f1ff] px-1.5 py-0.5 text-[10px] font-semibold text-[#0d6efd]">
            {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} active
          </span>
        )}
      </div>
    </section>
  );
}

function UnmappedWarning({
  unmapped,
  clientById,
  onDone,
}: {
  unmapped: Loc[];
  clientById: Map<string, Client>;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(true);
  const geocode = useServerFn(crmGeocodeUnmapped);
  const qc = useQueryClient();
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<{ updated: number; failed: number } | null>(null);

  // Group by client
  const grouped = useMemo(() => {
    const m = new Map<string, Loc[]>();
    unmapped.forEach((l) => {
      const arr = m.get(l.client_id) ?? [];
      arr.push(l);
      m.set(l.client_id, arr);
    });
    return Array.from(m.entries())
      .map(([id, locs]) => ({ id, client: clientById.get(id), locs }))
      .sort((a, b) => (a.client?.name ?? "").localeCompare(b.client?.name ?? ""));
  }, [unmapped, clientById]);

  const runGeocode = useCallback(async () => {
    if (running) return;
    setRunning(true);
    setResult(null);
    const ids = unmapped.map((l) => l.id);
    const CHUNK = 20;
    let updated = 0;
    let failed = 0;
    setProgress({ done: 0, total: ids.length });
    try {
      for (let i = 0; i < ids.length; i += CHUNK) {
        const slice = ids.slice(i, i + CHUNK);
        const r = await geocode({ data: { ids: slice } });
        updated += r.updated;
        failed += r.failed;
        setProgress({ done: Math.min(i + slice.length, ids.length), total: ids.length });
      }
      setResult({ updated, failed });
      await qc.invalidateQueries({ queryKey: ["crm", "map"] });
      onDone();
    } finally {
      setRunning(false);
    }
  }, [geocode, onDone, qc, running, unmapped]);

  return (
    <section className="rounded-xl border border-amber-200 bg-amber-50/70 shadow-sm">
      <div className="w-full flex items-center gap-2 px-3 sm:px-4 py-2.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-left flex-1 min-w-0"
        >
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
          <span className="text-sm font-semibold text-amber-900">
            {unmapped.length} location{unmapped.length === 1 ? "" : "s"} missing coordinates
          </span>
          <span className="hidden sm:inline text-xs text-amber-700/80 truncate">
            — not shown on the map.
          </span>
        </button>
        <button
          type="button"
          onClick={runGeocode}
          disabled={running || unmapped.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-60"
        >
          {running ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          {running
            ? progress
              ? `Geocoding ${progress.done}/${progress.total}…`
              : "Geocoding…"
            : "Geocode all"}
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-xs font-semibold text-amber-800"
        >
          {open ? "Hide" : "Show"}
        </button>
      </div>
      {result && (
        <div className="px-3 sm:px-4 pb-2 text-xs text-amber-900">
          Geocoded <span className="font-semibold">{result.updated}</span> ·{" "}
          <span className="font-semibold">{result.failed}</span> failed
          {result.failed > 0 ? " (add addresses or coordinates manually)" : ""}.
        </div>
      )}
      {open && (
        <ul className="divide-y divide-amber-200/70 border-t border-amber-200/70">
          {grouped.map(({ id, client, locs }) => (
            <li key={id} className="px-3 sm:px-4 py-2 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-amber-950 truncate">
                  {client?.name ?? "Unknown client"}
                </div>
                <div className="text-xs text-amber-800/80 truncate">
                  {locs
                    .map((l) => l.label + (l.address ? ` — ${l.address}` : ""))
                    .join(" · ")}
                </div>
              </div>
              {client && (
                <Link
                  to="/crm/$clientId"
                  params={{ clientId: client.id }}
                  className="shrink-0 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100"
                >
                  <Pencil className="h-3 w-3" />
                  Add coordinates
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
